/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
export type Delimiter = RegExp | string;

export interface MakePreviewOptions {
  maxTokensToShow: number;
  maxTokenLength: number;
  keepTokenTailLength: number;
  maxTotalLength: number;
  delimiter: Delimiter;
}

export type MakePreviewConfig = Partial<MakePreviewOptions>;

export const defaultMakePreviewOptions: MakePreviewOptions = {
  maxTokensToShow     : 2,
  maxTokenLength      : 18,
  keepTokenTailLength : 4,
  maxTotalLength      : 48,
  delimiter           : /[,\s]+/
};

/**
 * Shortens a token to fit within specified length constraints.
 *
 * @param   token The token to shorten.
 * @param   maxTokenLength The maximum allowed length for the token.
 * @param   keepTokenTailLength The length of the token tail to preserve.
 * @returns The shortened token.
 */
export const shortenToken = function(
  token: string,
  maxTokenLength: number,
  keepTokenTailLength: number
): string {
  if (token?.length <= maxTokenLength) {
    return token;
  }
  const headLen = Math.max(maxTokenLength - keepTokenTailLength - 3, 1);
  const head    = token.slice(0, headLen);
  const tail   = token.slice(-keepTokenTailLength);
  return `${head}...${tail}`;
};

/**
 * Joins an array of tokens into a single string with a limit on the number 
 * of tokens shown.
 *
 * @param   tokens - array of tokens to join
 * @param   maxTokensToShow - maximum number of tokens to display
 * @returns joined string of tokens
 */
export const joinTokensWithLimit = function(
  tokens: string[],
  maxTokensToShow: number
): string {
  const shown = tokens?.slice(0, maxTokensToShow)?.join?.(', ');
  return tokens?.length > maxTokensToShow ? `${shown}...` : shown;
};

/**
 * Clamps the length of a text string to a maximum total length.
 *
 * @param   text The text to clamp.
 * @param   maxTotalLength The maximum allowed length for the text.
 * @returns The clamped text.
 */
export const clampLength = function(text: string, maxTotalLength: number): string {
  if (text.length <= maxTotalLength) {
    return text;
  }
  return `${text.slice(0, maxTotalLength - 1)}…`;
};

/**
 * Generates a preview of the input text based on the provided configuration.
 * 
 * ```typescript
 * import {makePreview, MakePreviewConfig} from '@ion/dls/src/components/search/SearchUtils';
 * 
 * const previewConfig: MakePreviewConfig = { maxTokensToShow: 3 };
 * const preview = makePreview('Sample input text for preview generation', previewConfig);
 * ```
 *
 * @param   rawInput The raw input text to preview.
 * @param   config   Optional configuration for the preview.
 * @returns The generated preview text.
 */
export const makePreview = function(rawInput: string, config: MakePreviewConfig = {}): string {
  if (rawInput) {
    const options = {...defaultMakePreviewOptions, ...config};
    const tokens = String(rawInput)
      ?.split(options?.delimiter)
      ?.filter(Boolean)
      ?.map(token => shortenToken(token, options?.maxTokenLength, options?.keepTokenTailLength));
    const limited = joinTokensWithLimit(tokens, options.maxTokensToShow);
    return clampLength(limited, options.maxTotalLength);
  }
  return '';
};
